import React, { useRef, useEffect, useState } from "react"

import { getAppMAUCount, getTotalUsersInDbCount } from "../../../api/saas/app"
import SaasTooltip from "../SaasTooltip"
import SelectInput from "../SelectInput"

import "../../../style/users-tab.module.css"
import { getUserCountForMultitenantAppId } from "../../../api/saas/multi-tenancy/connection-uri-domain/appid/users/count"
import { getMAUForMultiTenancyApp } from "../../../api/saas/multi-tenancy/connection-uri-domain/appid/users/active"
import { Spinner } from "../../../useOss"

interface Props {
  deploymentId: string,
  appDeploymentId: string,
  setDaysCallback: Function,
  paginationLinkClickCallback: React.MouseEventHandler<HTMLAnchorElement>,
  activeSessionsExplanationAction: Function,
  coreVersion: string | undefined,
  refetchEventName: string;
  deploymentType: "development" | "production",
  usesMultiTenancy: boolean,
}

const ProdUsersTab = (props: Props) => {
  // Ref for tooltip
  const tipRef = useRef(null)

  // States
  const [mauCount, setMauCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [totalCountStatus, setTotalCountStatus] = useState<'LOADING'| 'COMPLETED'>('LOADING')
  const [mauCountStatus, setMauCountStatus] = useState<'LOADING'| 'COMPLETED'>('LOADING')
  const [days, setDays] = useState<string | number>("Current billing period")

  useEffect(() => {
    getCount(days, true)

    document.addEventListener(props.refetchEventName, refetchEventAction);

    return () => {
      document.removeEventListener(props.refetchEventName, refetchEventAction);
    }
  }, [])

  const refetchEventAction = async () => {
    await getCount(days, true);
  }

  const getCountValueFromResult = (result: { count: number }) => {
    return result !== undefined && result.count !== undefined ? result.count : 0
  }

  const getMAUCount = async (dayCount: number | string): Promise<{
    count: number;
  }> => {
    if (props.usesMultiTenancy === true) {

      if (typeof dayCount === "string") {
        const response = await getMAUForMultiTenancyApp({
          deploymentId: props.deploymentId,
          appId: props.appDeploymentId,
          deploymentType: props.deploymentType,
          //when days is passed as -1, active users for current month will be returned from the API.
          days: -1,
        })

        return response;
      }

      const response = await getMAUForMultiTenancyApp({
        deploymentId: props.deploymentId,
        appId: props.appDeploymentId,
        deploymentType: props.deploymentType,
        days: dayCount,
      })

      return response;
    } else {

      if (typeof dayCount === "string") {
        //when days is passed as -1, active users for current month will be returned from the API.
        const response = await getAppMAUCount(props.deploymentId, props.appDeploymentId, -1)
        return {
          count: response.count,
        };
      }

      const response = await getAppMAUCount(props.deploymentId, props.appDeploymentId, dayCount)
      return {
        count: response.count,
      };
    }
  }

  const getTotalUsers = async () => {
    if (props.usesMultiTenancy === true) {
      let response = await getUserCountForMultitenantAppId({
        deploymentId: props.deploymentId,
        appId: props.appDeploymentId,
        deploymentType: props.deploymentType,
      })

      return response;
    } else {
      let response = await getTotalUsersInDbCount(props.deploymentId, props.appDeploymentId)
      return response;
    }
  }

  const getCount = async (dayCount: number | string, initial?: boolean) => {
    if (props.deploymentId && props.appDeploymentId) {
      let newMauCount = 0
      let newTotalCount = 0

      if (initial) {
        // if it is the initial or the first call of this function on mount
        // we fetch both the MAUs and the total number of users
        setTotalCountStatus('LOADING');
        setMauCountStatus('LOADING')
        const results = await Promise.all([
          getMAUCount(dayCount),
          getTotalUsers(),
        ])
        newMauCount = getCountValueFromResult(results[0])
        newTotalCount = getCountValueFromResult(results[1])
        setTotalCount(newTotalCount)
        setTotalCountStatus('COMPLETED')
      } else {
        // if it is not the initial call, i.e. it is called when the user changes the number of days
        // then we don't fetch the total users
        setMauCountStatus('LOADING')
        const result = await getMAUCount(dayCount)
        newMauCount = getCountValueFromResult(result)
        newTotalCount = totalCount
      }

      setMauCount(newMauCount)
      setMauCountStatus('COMPLETED')
    }

    if (!initial) {
      props.setDaysCallback(dayCount)
    }
  }

  const setDayCount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const numValue = parseInt(value)
    if (Number.isNaN(numValue) || numValue < 0) {
      setDays(0)
    } else if (numValue > 365) {
      setDays(365)
    } else {
      setDays(numValue)
    }
  }

  // inputBlur parameter is set to true when this function is called from handleInputBlur
  const validateAndGetUsers = (newDays: number, inputBlur: boolean) => {
    let daysCount = 7
    if (!(Number.isNaN(newDays) || newDays <= 0)) {
      daysCount = newDays
    }

    if (daysCount !== days || inputBlur) {
      setDays(daysCount)
      getCount(daysCount)
    }
  }

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement> | { target: { value: string } }) => {
    const { value } = e.target
    const numValue = parseInt(value)
    validateAndGetUsers(numValue, true)
  }

  let apiDomainForUserDashboard = "api.yourdomain.com";
  let apiBasePathForUserDashboard = "/auth"

  const appInfoFromStorage = window.localStorage.getItem("form_appInfo");

  if (appInfoFromStorage !== null) {
    try {
      // The try is just a precaution
      const parsedAppInfo = JSON.parse(appInfoFromStorage);
      const _apiDomain = parsedAppInfo["apiDomain"];
      const _apiBasePath = parsedAppInfo["apiBasePath"];

      if (_apiDomain !== undefined) {
        apiDomainForUserDashboard = _apiDomain;
      }

      if (_apiBasePath !== undefined) {
        apiBasePathForUserDashboard = _apiBasePath;
      }
    } catch (_) { }
  }

  let userDashboardUrl = apiDomainForUserDashboard + apiBasePathForUserDashboard + "/dashboard";

  let shouldShowCountUpdatedText = false;

  if (props.coreVersion !== undefined && parseInt(props.coreVersion.split(".")[0]) < 5) {
    shouldShowCountUpdatedText = true;
  }

  return (
    <div className="users-tab">

      <div className="user-tab-user-dashboard-banner-container">
        {
          <span>The user management dashboard is available on <span className="user-tab-user-dashboard-banner-url">{userDashboardUrl}.</span> <a className="underlined" href="/docs/userdashboard/about">Learn More</a></span>
          /* <div className="user-tab-user-dashboard-banner-button">
            View Now
          </div> */
        }
      </div>

      {
        shouldShowCountUpdatedText &&
        <p className="users-tab__update-duration">Updated every 30 minutes</p>
      }

      <div className="users-tab__counts">
        <div className="users-tab__counts__total">
          <p className="users-tab__counts__total__p">
            Total no. of users:
          </p>
          <div className="users-tab__counts__total__count">
            {totalCountStatus === 'LOADING'? <Spinner isInline />: totalCount.toLocaleString()}
          </div>
        </div>

        <div className="users-tab__counts__mau">
          <div className="users-tab__counts__mau__p">
            <span>No. of active users in {isNaN(Number(days)) === false ? "last" : ""}</span>
            <SelectInput
              value={days}
              setValue={setDayCount}
              onBlur={handleInputBlur}
              handleSelect={validateAndGetUsers}
              selectOptions={[
                { id: '1', value: "Current billing period", placeholder: "Current billing period" },
                { id: '2', value: 7, placeholder: "7" },
                { id: '3', value: 30, placeholder: "30" },
                { id: '4', value: 90, placeholder: "90" },
                { id: '5', value: 120, placeholder: "120" },
                { id: '6', value: 180, placeholder: "180" }
              ]}
            />
            {isNaN(Number(days)) === false ? "days" : ""}:
          </div>
          <div className="users-tab__counts__mau__count">
            {mauCountStatus === 'LOADING'? <Spinner isInline />: <>{mauCount.toLocaleString()}</>}
            {/* This tooltip needs to be rendered at all times since its reference is used in SaasToolTip*/}
            <img
              src="static/assets/dashboard/question-round.png"
              alt="Connection tips"
              ref={tipRef}
              style={{
                display: mauCountStatus === 'LOADING'? 'none': 'inline-block'
              }}
            />
          </div>
        </div>
      </div>

      <SaasTooltip
        maxWidth="28.125rem"
        trigger="hover"
        elementRef={tipRef}
        showTooltipCallback={props.activeSessionsExplanationAction}
      >
        <p style={{ textAlign: "center" }}>
          An active user is a user who has created or refreshed a session at least once in the given time period
        </p>
      </SaasTooltip>
    </div>
  )
}

export default ProdUsersTab
